import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Swal from "sweetalert2";
import { getSetting, updateSetting } from "../../Services/Setting";
import {
  getPayoutRequests,
  getPayoutHistory,
  getPendingDrivers,
  handlePayoutRequests,
  processBatchPayout,
  getAutoPayoutStatus,
} from "../../Services/DriverPayout";
import { adminSwalOptions } from "../../Utils/adminSwal";
import { Success, Error } from "../../Components/Modal";
import moment from "moment";
import PageHeader from "../../Components/PageHeader";
import ProcessPayoutModal from "./Components/ProcessPayoutModal";
import PayoutResultModal from "./Components/PayoutResultModal";
import PayoutHistoryDetailModal from "./Components/PayoutHistoryDetailModal";
import AcceptPayoutRequestModal from "./Components/AcceptPayoutRequestModal";
import RejectPayoutRequestModal from "./Components/RejectPayoutRequestModal";
import {
  formatCurrency,
  formatScheduleLabel,
  mapPayoutRequest,
  mapPendingDriver,
  mapHistoryItem,
  generateReferenceId,
  SOURCE_LABELS,
} from "./utils";
import "../CMSManagement/CMSManagement.css";
import "./DriverPayouts.css";

const PAGE_SIZE = 50;

const TABS = [
  { id: "requests", label: "Payout Requests", icon: "fa-inbox" },
  { id: "pending", label: "Pending Payouts", icon: "fa-clock" },
  { id: "history", label: "Payout History", icon: "fa-history" },
];

export default function DriverPayouts() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("requests");
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestSearch, setRequestSearch] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [resultModal, setResultModal] = useState({ show: false, type: null, data: null });
  const [historyDetail, setHistoryDetail] = useState(null);
  const [acceptRequest, setAcceptRequest] = useState(null);
  const [rejectRequest, setRejectRequest] = useState(null);
  const [requestsLimit, setRequestsLimit] = useState(PAGE_SIZE);
  const [historyLimit, setHistoryLimit] = useState(PAGE_SIZE);

  const { data: settingData } = useQuery("get-setting", getSetting);
  const settingsObj = settingData?.data?.data;
  const scheduleLabel = useMemo(() => formatScheduleLabel(settingsObj), [settingsObj]);

  const {
    data: requestsData,
    isLoading: requestsLoading,
    isFetching: requestsFetching,
  } = useQuery(
    ["driver-payout-requests", requestSearch, requestsLimit],
    () => getPayoutRequests(1, requestsLimit, requestSearch, "pending,processing"),
    { keepPreviousData: true, refetchOnWindowFocus: true }
  );

  const {
    data: pendingData,
    isLoading: pendingLoading,
    isFetching: pendingFetching,
  } = useQuery(
    ["driver-pending-drivers", searchQuery],
    () => getPendingDrivers(1, 100, searchQuery),
    { keepPreviousData: true, refetchOnWindowFocus: true }
  );

  const {
    data: historyData,
    isLoading: historyLoading,
    isFetching: historyFetching,
  } = useQuery(
    ["driver-payout-history", historyLimit],
    () => getPayoutHistory(1, historyLimit),
    { keepPreviousData: true, refetchOnWindowFocus: true }
  );

  const payoutRequests = useMemo(
    () => (requestsData?.data?.data?.items || []).map(mapPayoutRequest),
    [requestsData]
  );

  const pendingDrivers = useMemo(
    () => (pendingData?.data?.data?.items || []).map(mapPendingDriver),
    [pendingData]
  );

  const payoutHistory = useMemo(
    () =>
      (historyData?.data?.data?.items || []).map((item) =>
        mapHistoryItem(item, scheduleLabel)
      ),
    [historyData, scheduleLabel]
  );

  const hasMoreRequests = !!requestsData?.data?.pagination?.hasNextPage;
  const hasMoreHistory = !!historyData?.data?.pagination?.hasNextPage;

  const { data: autoPayoutData } = useQuery("driver-auto-payout-status", getAutoPayoutStatus, {
    refetchOnWindowFocus: true,
  });
  const autoPayout = autoPayoutData?.data?.data;

  const autoPayoutMutation = useMutation((enabled) => updateSetting({ autoPayoutEnabled: enabled }), {
    onSuccess: () => {
      queryClient.invalidateQueries("driver-auto-payout-status");
      queryClient.invalidateQueries("get-setting");
    },
    onError: (err) => Error(err?.response?.data?.message || "Unable to update automatic payout."),
  });

  const toggleAutoPayout = async () => {
    const turningOn = !autoPayout?.enabled;
    const choice = await Swal.fire({
      ...adminSwalOptions,
      title: turningOn ? "Turn on automatic payouts?" : "Turn off automatic payouts?",
      html: `<p class="admin-swal-message">${
        turningOn
          ? `On every payout day (${scheduleLabel}) all drivers with a verified bank are paid automatically, including open payout requests.`
          : "Drivers will only be paid when an admin processes payouts from this page."
      }</p>`,
      showCancelButton: true,
      confirmButtonText: turningOn ? "Turn on" : "Turn off",
      cancelButtonText: "Cancel",
    });
    if (choice.isConfirmed) autoPayoutMutation.mutate(turningOn);
  };

  const invalidatePayoutQueries = () => {
    queryClient.invalidateQueries("driver-payout-requests");
    queryClient.invalidateQueries("driver-payout-history");
    queryClient.invalidateQueries("driver-pending-drivers");
  };

  const acceptMutation = useMutation(
    ({ requestId, request }) =>
      handlePayoutRequests("approve", [requestId]).then((res) => ({ res, request })),
    {
      onSuccess: ({ res, request }) => {
        setAcceptRequest(null);
        invalidatePayoutQueries();

        const processed = res.data?.data?.processed?.[0];
        if (!processed?.success) {
          showAcceptFailure(request, res.data?.data?.failed?.[0]?.message || res.data?.message);
          return;
        }

        setResultModal({
          show: true,
          type: "success",
          data: {
            referenceId: processed.transferId || `PAY-${String(request.id).slice(-8).toUpperCase()}`,
            driverCount: 1,
            totalAmount: request?.requestedAmount || processed.amount,
            processedAt: new Date().toISOString(),
            driverName: request?.name,
          },
        });
      },
      onError: (err, { request }) => {
        setAcceptRequest(null);
        invalidatePayoutQueries();
        showAcceptFailure(request, err?.response?.data?.message);
      },
    }
  );

  function showAcceptFailure(request, message) {
    const reason = message || "Payout could not be approved.";
    setResultModal({
      show: true,
      type: "failed",
      data: {
        failureReason: reason,
        failedDrivers: request ? [{ id: request.driverId, name: request.name, blockReason: reason }] : [],
        partialSuccess: false,
        retry: null,
      },
    });
  }

  const rejectMutation = useMutation(
    ({ requestId, reason }) => handlePayoutRequests("reject", [requestId], reason),
    {
      onSuccess: (res) => {
        setRejectRequest(null);
        invalidatePayoutQueries();
        Success("Request Rejected", res.data?.message || "Payout request rejected.");
      },
      onError: (err) => {
        Error(err?.response?.data?.message || "Unable to reject payout request.");
      },
    }
  );

  const batchMutation = useMutation(
    ({ driverIds, drivers }) => processBatchPayout(driverIds).then((res) => ({ res, drivers })),
    {
      onSuccess: ({ res, drivers }, { driverIds }) => {
        setShowConfirmModal(false);
        invalidatePayoutQueries();

        const summary = res.data?.data?.summary || {};
        const failed = res.data?.data?.failed || [];
        const processed = res.data?.data?.processed || [];
        const paidIds = new Set(processed.map((p) => String(p.driverId)));
        setSelectedIds((prev) => prev.filter((id) => !paidIds.has(String(id))));

        const failedDrivers = failed.map((f) => ({
          id: f.driverId,
          name: drivers.find((d) => String(d.id) === String(f.driverId))?.name || "Driver",
          blockReason: f.message,
        }));

        if (summary.failedCount > 0) {
          setResultModal({
            show: true,
            type: "failed",
            data: {
              failureReason:
                summary.processedCount === 0
                  ? failed[0]?.message || "Batch payout failed for all selected drivers."
                  : `${summary.failedCount} driver(s) could not be paid.`,
              failedDrivers,
              partialSuccess: summary.processedCount > 0,
              successCount: summary.processedCount,
              retry: "batch",
            },
          });
          return;
        }

        setResultModal({
          show: true,
          type: "success",
          data: {
            referenceId: generateReferenceId(),
            driverCount: summary.processedCount || processed.length || driverIds.length,
            totalAmount: summary.totalAmount,
            processedAt: new Date().toISOString(),
          },
        });
      },
      onError: (err, { drivers }) => {
        setShowConfirmModal(false);
        invalidatePayoutQueries();
        const failed = err?.response?.data?.data?.failed || [];
        const reason = err?.response?.data?.message || "Unable to process batch payout.";
        setResultModal({
          show: true,
          type: "failed",
          data: {
            failureReason: reason,
            failedDrivers: failed.length
              ? failed.map((f) => ({
                  id: f.driverId,
                  name: drivers.find((d) => String(d.id) === String(f.driverId))?.name || "Driver",
                  blockReason: f.message,
                }))
              : drivers.map((d) => ({ id: d.id, name: d.name, blockReason: reason })),
            partialSuccess: false,
            retry: "batch",
          },
        });
      },
    }
  );

  const eligibleDrivers = useMemo(
    () => pendingDrivers.filter((d) => d.walletBalance > 0),
    [pendingDrivers]
  );

  const payableDrivers = useMemo(
    () => eligibleDrivers.filter((d) => !d.payoutBlocked),
    [eligibleDrivers]
  );

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => payableDrivers.some((d) => d.id === id)));
  }, [payableDrivers]);

  const filteredPending = eligibleDrivers;

  const selectedDrivers = useMemo(
    () => payableDrivers.filter((d) => selectedIds.includes(d.id)),
    [payableDrivers, selectedIds]
  );

  const hasBlockedSelection = useMemo(
    () => selectedIds.some((id) => eligibleDrivers.find((d) => d.id === id)?.payoutBlocked),
    [selectedIds, eligibleDrivers]
  );

  const totalPendingAmount = useMemo(
    () =>
      Number(pendingData?.data?.data?.summary?.totalAmount) ||
      eligibleDrivers.reduce((sum, d) => sum + d.walletBalance, 0),
    [pendingData, eligibleDrivers]
  );

  const selectedTotal = useMemo(
    () => selectedDrivers.reduce((sum, d) => sum + d.walletBalance, 0),
    [selectedDrivers]
  );

  const filteredRequests = payoutRequests;

  const totalRequestAmount = useMemo(
    () =>
      Number(requestsData?.data?.data?.summary?.totalAmount) ||
      payoutRequests.reduce((sum, r) => sum + r.requestedAmount, 0),
    [requestsData, payoutRequests]
  );

  const lastPayout = payoutHistory[0];

  const allFilteredSelected =
    payableDrivers.length > 0 &&
    payableDrivers.every((d) => selectedIds.includes(d.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(payableDrivers.map((d) => d.id));
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      const newIds = new Set([...selectedIds, ...payableDrivers.map((d) => d.id)]);
      setSelectedIds([...newIds]);
    }
  };

  const toggleDriver = (id, payoutBlocked) => {
    if (payoutBlocked) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openConfirmModal = () => {
    if (selectedDrivers.length === 0) {
      Error(
        hasBlockedSelection
          ? "Selected drivers must have a verified bank account before payout can be processed."
          : "Select at least one driver with a verified bank account."
      );
      return;
    }
    setShowConfirmModal(true);
  };

  const processPayout = () => {
    if (selectedDrivers.length === 0) {
      Error("Payout cannot be processed without a verified bank account.");
      return;
    }
    batchMutation.mutate({
      driverIds: selectedDrivers.map((d) => d.id),
      drivers: selectedDrivers,
    });
  };

  const closeResultModal = () => {
    setResultModal({ show: false, type: null, data: null });
  };

  const retryAfterFailure = () => {
    closeResultModal();
    setShowConfirmModal(true);
  };

  const confirmAcceptRequest = () => {
    if (!acceptRequest) return;
    if (acceptRequest.payoutBlocked) {
      Error(acceptRequest.blockReason || "Bank account must be linked and verified before payout.");
      return;
    }
    acceptMutation.mutate({ requestId: acceptRequest.id, request: acceptRequest });
  };

  const confirmRejectRequest = (reason) => {
    if (!rejectRequest) return;
    rejectMutation.mutate({ requestId: rejectRequest.id, reason });
  };

  const isTableLoading =
    (activeTab === "requests" && requestsLoading) ||
    (activeTab === "pending" && (pendingLoading || pendingFetching)) ||
    (activeTab === "history" && historyLoading);

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="user-details-wrapper mt-0">
              <PageHeader
                title="Driver Payouts"
                subtitle={`${scheduleLabel} — driver wallet payouts`}
                action={
                  <div className="dp-week-badge">
                    <i className="far fa-calendar-alt" />
                    {scheduleLabel}
                  </div>
                }
              />

              <div className="dp-stats-row dp-stats-row--4">
                <div className="dp-stat-card">
                  <div className="dp-stat-card__icon dp-stat-card__icon--orange">
                    <i className="fas fa-wallet" />
                  </div>
                  <div>
                    <span className="dp-stat-card__label">Total Pending</span>
                    <strong className="dp-stat-card__value">
                      {formatCurrency(totalPendingAmount)}
                    </strong>
                  </div>
                </div>
                <div className="dp-stat-card">
                  <div className="dp-stat-card__icon dp-stat-card__icon--purple">
                    <i className="fas fa-inbox" />
                  </div>
                  <div>
                    <span className="dp-stat-card__label">Payout Requests</span>
                    <strong className="dp-stat-card__value">
                      {requestsData?.data?.data?.summary?.totalRequestCount ?? payoutRequests.length}
                    </strong>
                    {payoutRequests.length > 0 && (
                      <span className="dp-stat-card__meta">
                        {formatCurrency(totalRequestAmount)} requested
                      </span>
                    )}
                  </div>
                </div>
                <div className="dp-stat-card">
                  <div className="dp-stat-card__icon dp-stat-card__icon--blue">
                    <i className="fas fa-users" />
                  </div>
                  <div>
                    <span className="dp-stat-card__label">Drivers Ready</span>
                    <strong className="dp-stat-card__value">{payableDrivers.length}</strong>
                    {eligibleDrivers.length > payableDrivers.length && (
                      <span className="dp-stat-card__meta">
                        {eligibleDrivers.length - payableDrivers.length} missing bank details
                      </span>
                    )}
                  </div>
                </div>
                <div className="dp-stat-card">
                  <div className="dp-stat-card__icon dp-stat-card__icon--green">
                    <i className="fas fa-check-circle" />
                  </div>
                  <div>
                    <span className="dp-stat-card__label">Last Payout</span>
                    <strong className="dp-stat-card__value dp-stat-card__value--sm">
                      {lastPayout ? formatCurrency(lastPayout.totalAmount) : "—"}
                    </strong>
                    {lastPayout && (
                      <span className="dp-stat-card__meta">
                        {moment(lastPayout.processedAt).format("MMM D, YYYY")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {autoPayout && (
                <div className={`dp-auto-bar ${autoPayout.enabled ? "" : "dp-auto-bar--off"}`}>
                  <div className="dp-auto-bar__icon">
                    <i className={`fas ${autoPayout.enabled ? "fa-robot" : "fa-pause"}`} />
                  </div>
                  <div className="dp-auto-bar__body">
                    <strong>
                      Automatic payout {autoPayout.enabled ? "is on" : "is off"}
                    </strong>
                    <span>
                      {autoPayout.enabled && autoPayout.nextRunDate
                        ? `Next run ${moment(autoPayout.nextRunDate).format("ddd, MMM D")} at ${moment()
                            .hour(autoPayout.runHour)
                            .minute(0)
                            .format("h:mm A")} (${autoPayout.timezone})`
                        : "Drivers are paid only when you process payouts here."}
                    </span>
                    {autoPayout.lastRun && (
                      <span className={autoPayout.lastRun.failedCount ? "dp-auto-bar__warn" : ""}>
                        Last run {moment(autoPayout.lastRun.date).format("MMM D")}: paid{" "}
                        {autoPayout.lastRun.processedCount} driver(s){" "}
                        {formatCurrency(autoPayout.lastRun.totalAmount)}
                        {autoPayout.lastRun.failedCount ? ` · ${autoPayout.lastRun.failedCount} failed` : ""}
                        {autoPayout.lastRun.notAttemptedCount
                          ? ` · ${autoPayout.lastRun.notAttemptedCount} not attempted`
                          : ""}
                        {autoPayout.lastRun.stoppedReason ? ` — ${autoPayout.lastRun.stoppedReason}` : ""}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="dp-select-all-btn"
                    onClick={toggleAutoPayout}
                    disabled={autoPayoutMutation.isLoading}
                  >
                    {autoPayout.enabled ? "Turn off" : "Turn on"}
                  </button>
                </div>
              )}

              <div className="dp-tabs">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`dp-tab ${activeTab === tab.id ? "dp-tab--active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={`fas ${tab.icon}`} />
                    {tab.label}
                    {tab.id === "requests" && payoutRequests.length > 0 && (
                      <span className="dp-tab__count">{payoutRequests.length}</span>
                    )}
                    {tab.id === "pending" && payableDrivers.length > 0 && (
                      <span className="dp-tab__count">{payableDrivers.length}</span>
                    )}
                  </button>
                ))}
              </div>

              {isTableLoading && (
                <div className="dp-loading">
                  <span className="dp-spinner" />
                  Loading…
                </div>
              )}

              {activeTab === "requests" && !isTableLoading && (
                <div className="dp-panel">
                  <div className="dp-panel__toolbar">
                    <div className="dp-search">
                      <i className="fas fa-search" />
                      <input
                        type="text"
                        placeholder="Search payout requests…"
                        value={requestSearch}
                        onChange={(e) => setRequestSearch(e.target.value)}
                      />
                    </div>
                    <span className="dp-requests-hint">
                      <i className="fas fa-mobile-alt" />
                      Drivers submit requests from the app
                    </span>
                  </div>

                  {filteredRequests.length === 0 ? (
                    <div className="dp-empty">
                      <i className="fas fa-inbox" />
                      <h5>No payout requests</h5>
                      <p>When drivers request a withdrawal, they will appear here.</p>
                    </div>
                  ) : (
                    <div className="dp-table-wrap">
                      <table className="dp-table">
                        <thead>
                          <tr>
                            <th>Driver</th>
                            <th>Requested Amount</th>
                            <th>Remaining Wallet</th>
                            <th>Bank Account</th>
                            <th>Requested On</th>
                            <th>Note</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRequests.map((req) => (
                            <tr key={req.id}>
                              <td>
                                <div className="dp-driver-cell">
                                  <div className="dp-driver-cell__avatar">
                                    {req.name.charAt(0)}
                                  </div>
                                  <div>
                                    <span className="dp-driver-cell__name">{req.name}</span>
                                    <span className="dp-request-contact">{req.email}</span>
                                    {req.payoutBlocked && (
                                      <span className="dp-driver-cell__warning">
                                        <i className="fas fa-exclamation-circle" />
                                        {req.blockReason || "Bank not verified"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="dp-table__amount">
                                <strong>{formatCurrency(req.requestedAmount)}</strong>
                                {req.failureReason && req.status === "pending" && (
                                  <span className="dp-driver-cell__warning" title={req.failureReason}>
                                    <i className="fas fa-exclamation-circle" />
                                    Last attempt failed
                                  </span>
                                )}
                              </td>
                              <td>{formatCurrency(req.walletBalance)}</td>
                              <td>
                                {req.bankLast4 ? (
                                  <span className="dp-bank-tag">
                                    <i className="fas fa-university" />
                                    {req.bankName} •••• {req.bankLast4}
                                  </span>
                                ) : (
                                  <span className="dp-bank-tag dp-bank-tag--missing">
                                    Not linked
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="dp-request-date">
                                  {moment(req.requestedAt).format("MMM D, YYYY")}
                                </span>
                                <span className="dp-request-time">
                                  {moment(req.requestedAt).format("h:mm A")}
                                </span>
                              </td>
                              <td>
                                <span className="dp-request-note-cell">
                                  {req.note || "—"}
                                </span>
                              </td>
                              <td>
                                {req.status === "processing" ? (
                                  <span className="dp-status-badge dp-status-badge--processing">
                                    <span className="dp-spinner" />
                                    Processing
                                  </span>
                                ) : (
                                <div className="dp-request-actions">
                                  <button
                                    type="button"
                                    className="dp-accept-btn"
                                    onClick={() => setAcceptRequest(req)}
                                    disabled={acceptMutation.isLoading || req.payoutBlocked}
                                    title={
                                      req.payoutBlocked
                                        ? req.blockReason || "Bank account required"
                                        : "Accept payout request"
                                    }
                                  >
                                    <i className="fas fa-check" />
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="dp-reject-btn"
                                    onClick={() => setRejectRequest(req)}
                                    disabled={rejectMutation.isLoading}
                                  >
                                    <i className="fas fa-times" />
                                    Reject
                                  </button>
                                </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {hasMoreRequests && (
                    <div className="dp-panel__footer">
                      <button
                        type="button"
                        className="dp-select-all-btn"
                        disabled={requestsFetching}
                        onClick={() => setRequestsLimit((n) => n + PAGE_SIZE)}
                      >
                        {requestsFetching ? "Loading…" : "Load more"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "pending" && !isTableLoading && (
                <div className="dp-panel">
                  <div className="dp-panel__toolbar">
                    <div className="dp-search">
                      <i className="fas fa-search" />
                      <input
                        type="text"
                        placeholder="Search drivers by name, email or phone…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    {selectedIds.length > 0 && (
                      <div className="dp-selection-bar">
                        <span>
                          {selectedIds.length} selected · {formatCurrency(selectedTotal)}
                        </span>
                        <button
                          type="button"
                          className="dp-process-btn"
                          onClick={openConfirmModal}
                          disabled={hasBlockedSelection || selectedDrivers.length === 0}
                        >
                          <i className="fas fa-paper-plane" />
                          Process Payout
                        </button>
                      </div>
                    )}
                  </div>

                  {filteredPending.length === 0 ? (
                    <div className="dp-empty">
                      <i className="fas fa-check-double" />
                      <h5>No pending payouts</h5>
                      <p>All driver wallets are cleared for this payout period.</p>
                    </div>
                  ) : (
                    <div className="dp-table-wrap">
                      <table className="dp-table">
                        <thead>
                          <tr>
                            <th className="dp-table__check">
                              <label className="dp-checkbox">
                                <input
                                  type="checkbox"
                                  checked={allFilteredSelected}
                                  onChange={toggleSelectAll}
                                  disabled={payableDrivers.length === 0}
                                />
                                <span />
                              </label>
                            </th>
                            <th>Driver</th>
                            <th>Contact</th>
                            <th>Bank Account</th>
                            <th>Deliveries</th>
                            <th className="dp-table__amount">Wallet Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPending.map((driver) => (
                            <tr
                              key={driver.id}
                              className={[
                                selectedIds.includes(driver.id) ? "dp-table__row--selected" : "",
                                driver.payoutBlocked ? "dp-table__row--blocked" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              <td className="dp-table__check">
                                <label className="dp-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(driver.id)}
                                    onChange={() => toggleDriver(driver.id, driver.payoutBlocked)}
                                    disabled={driver.payoutBlocked}
                                  />
                                  <span />
                                </label>
                              </td>
                              <td>
                                <div className="dp-driver-cell">
                                  <div className="dp-driver-cell__avatar">
                                    {driver.name.charAt(0)}
                                  </div>
                                  <div>
                                    <span className="dp-driver-cell__name">{driver.name}</span>
                                    {driver.payoutBlocked && (
                                      <span className="dp-driver-cell__warning">
                                        <i className="fas fa-exclamation-circle" />
                                        {driver.blockReason || "Bank not verified"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="dp-contact-cell">
                                  <span>{driver.email}</span>
                                  <span>{driver.phone}</span>
                                </div>
                              </td>
                              <td>
                                {driver.bankLast4 ? (
                                  <span className="dp-bank-tag">
                                    <i className="fas fa-university" />
                                    {driver.bankName} •••• {driver.bankLast4}
                                  </span>
                                ) : (
                                  <span className="dp-bank-tag dp-bank-tag--missing">
                                    Not linked
                                  </span>
                                )}
                              </td>
                              <td>{driver.ridesCompleted}</td>
                              <td className="dp-table__amount">
                                <strong>{formatCurrency(driver.walletBalance)}</strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {filteredPending.length > 0 && (
                    <div className="dp-panel__footer">
                      <button
                        type="button"
                        className="dp-select-all-btn"
                        onClick={toggleSelectAll}
                      >
                        {allFilteredSelected ? "Deselect All" : "Select All"}
                      </button>
                      <button
                        type="button"
                        className="dp-process-btn dp-process-btn--footer"
                        disabled={
                          selectedIds.length === 0 ||
                          batchMutation.isLoading ||
                          hasBlockedSelection ||
                          selectedDrivers.length === 0
                        }
                        onClick={openConfirmModal}
                      >
                        <i className="fas fa-paper-plane" />
                        Process Payout
                        {selectedIds.length > 0 && (
                          <span className="dp-process-btn__badge">
                            {formatCurrency(selectedTotal)}
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "history" && !isTableLoading && (
                <div className="dp-panel">
                  {payoutHistory.length === 0 ? (
                    <div className="dp-empty">
                      <i className="fas fa-history" />
                      <h5>No payout history</h5>
                      <p>Processed payouts will appear here.</p>
                    </div>
                  ) : (
                    <div className="dp-table-wrap">
                      <table className="dp-table">
                        <thead>
                          <tr>
                            <th>Reference</th>
                            <th>Source</th>
                            <th>Schedule</th>
                            <th>Date</th>
                            <th>Drivers</th>
                            <th className="dp-table__amount">Amount</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payoutHistory.map((batch) => (
                            <tr key={batch.id}>
                              <td>
                                <span className="dp-ref-id">{batch.referenceId}</span>
                              </td>
                              <td>
                                <span
                                  className={`dp-source-badge dp-source-badge--${batch.source || "admin_batch"}`}
                                >
                                  {SOURCE_LABELS[batch.source] || SOURCE_LABELS.admin_batch}
                                </span>
                              </td>
                              <td>{batch.weekLabel}</td>
                              <td>{moment(batch.processedAt).format("MMM D, YYYY")}</td>
                              <td>{batch.driverCount}</td>
                              <td className="dp-table__amount">
                                <strong>{formatCurrency(batch.totalAmount)}</strong>
                              </td>
                              <td>
                                <span className={`dp-status-badge dp-status-badge--${batch.status}`}>
                                  {batch.status}
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="dp-view-btn"
                                  onClick={() => setHistoryDetail(batch)}
                                >
                                  <i className="fas fa-eye" />
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {hasMoreHistory && (
                    <div className="dp-panel__footer">
                      <button
                        type="button"
                        className="dp-select-all-btn"
                        disabled={historyFetching}
                        onClick={() => setHistoryLimit((n) => n + PAGE_SIZE)}
                      >
                        {historyFetching ? "Loading…" : "Load more"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <ProcessPayoutModal
        show={showConfirmModal}
        onHide={() => !batchMutation.isLoading && setShowConfirmModal(false)}
        selectedDrivers={selectedDrivers}
        scheduleLabel={scheduleLabel}
        isProcessing={batchMutation.isLoading}
        onConfirm={processPayout}
      />

      <PayoutResultModal
        show={resultModal.show}
        onHide={closeResultModal}
        type={resultModal.type}
        result={resultModal.data}
        onRetry={resultModal.data?.retry === "batch" ? retryAfterFailure : undefined}
      />

      <PayoutHistoryDetailModal
        show={!!historyDetail}
        onHide={() => setHistoryDetail(null)}
        batch={historyDetail}
        scheduleLabel={scheduleLabel}
      />

      <AcceptPayoutRequestModal
        show={!!acceptRequest}
        onHide={() => !acceptMutation.isLoading && setAcceptRequest(null)}
        request={acceptRequest}
        isProcessing={acceptMutation.isLoading}
        onConfirm={confirmAcceptRequest}
      />

      <RejectPayoutRequestModal
        show={!!rejectRequest}
        onHide={() => !rejectMutation.isLoading && setRejectRequest(null)}
        request={rejectRequest}
        isProcessing={rejectMutation.isLoading}
        onConfirm={confirmRejectRequest}
      />
    </div>
  );
}
